"use client";

import { IProduct } from "@/types/product-d-t";
import { useState, useEffect } from "react";
import Loader from "../Loader";
import ProductItem from "./single-product/product-item";

type IProps = {
  trendingProd?: IProduct[];
  style_2?: boolean;
  container?: string;
};

function TrendingProducts({
  trendingProd = [],
  style_2 = false,
  container = "container",
}: IProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [visibleCount, setVisibleCount] = useState<number>(4);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [showAllMobile, setShowAllMobile] = useState<boolean>(false);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);

      if (mobile) {
        setVisibleCount(6);
      } else {
        setVisibleCount(8);
        setShowAllMobile(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSeeMore = () => {
    if (isMobile) {
      setShowAllMobile((prev) => !prev);
    } else {
      setLoading(true);
      setTimeout(() => {
        setVisibleCount(trendingProd.length);
        setLoading(false);
      }, 300);
    }
  };

  const productsToShow = isMobile
    ? showAllMobile
      ? trendingProd
      : trendingProd.slice(0, visibleCount)
    : trendingProd.slice(0, visibleCount);

  return (
    <>
      {loading && <Loader />}

      <section className="product__area themed-trending-section">
        <div className={container}>
          {/* TITLE */}
          <div className="row">
            <div className="col-12 text-center mb-50">
              {/* FLOATING GIFT ICONS */}
              <div className="gift-icons-wrapper">
                <span className="gift-icon icon-1">🎁</span>
                <span className="gift-icon icon-2">✨</span>
                <span className="gift-icon icon-3">🎀</span>
                <span className="gift-icon icon-4">🎉</span>
                <span className="gift-icon icon-5">🎊</span>
                <span className="gift-icon icon-6">💝</span>
              </div>

              <h2 className="animated-title">
                Trending Gift Collection
              </h2>

              <p className="animated-subtitle">
                Discover the most loved gifts of the season – perfect for birthdays, anniversaries, celebrations & special moments
              </p>
            </div>
          </div>

          {/* PRODUCTS GRID */}
          <div className={`row g-3 ${isMobile ? "mobile-grid" : ""}`}>
            {productsToShow.map((item: any, index: number) => {
              const firstImage = item.images?.[0]?.image || "/placeholder.png";
              const product = { ...item, image: firstImage };

              return (
                <div
                  key={index}
                  className={
                    isMobile
                      ? "col-6"
                      : "col-xl-3 col-lg-3 col-md-6 col-sm-6"
                  }
                >
                  <ProductItem product={product} setLoading={setLoading}/>
                </div>
              );
            })}
          </div>

          {/* SEE MORE */}
          {trendingProd.length > visibleCount && (
            <div className="row mt-40">
              <div className="col-12 text-center">
                <button
                  onClick={handleSeeMore}
                  disabled={loading}
                  className="see-more-btn-theme"
                >
                  <span className="btn-icon">🎁</span>
                  {loading
                    ? "Loading..."
                    : isMobile
                    ? showAllMobile
                      ? "Show Less"
                      : "Explore More Gifts"
                    : "Explore More Gifts"}
                </button>
              </div>
            </div>
          )}
        </div>

        <style jsx>{`
          .themed-trending-section {
            padding-top: 80px;
            padding-bottom: 60px;
            background: linear-gradient(135deg, #FFF5F5 0%, #FFFBF8 50%, #FFF5EE 100%);
            position: relative;
            overflow: hidden;
          }

          /* DECORATIVE BACKGROUND ELEMENTS */
          .themed-trending-section::before {
            content: "🎁";
            position: absolute;
            top: 50px;
            right: 80px;
            font-size: 120px;
            opacity: 0.05;
            pointer-events: none;
            animation: floatSlow 6s ease-in-out infinite;
          }

          .themed-trending-section::after {
            content: "🎉";
            position: absolute;
            bottom: 100px;
            left: 60px;
            font-size: 100px;
            opacity: 0.05;
            pointer-events: none;
            animation: floatSlow 7s ease-in-out infinite reverse;
          }

          @keyframes floatSlow {
            0%, 100% {
              transform: translateY(0) rotate(0deg);
            }
            50% {
              transform: translateY(-20px) rotate(5deg);
            }
          }

          /* FLOATING GIFT ICONS */
          .gift-icons-wrapper {
            position: relative;
            height: 60px;
            margin-bottom: 10px;
          }

          .gift-icon {
            position: absolute;
            font-size: 28px;
            opacity: 0;
            animation: floatGift 3s ease-in-out infinite;
            left: 50%;
            top: 0;
            filter: drop-shadow(0 4px 8px rgba(255,107,107,0.2));
          }

          .icon-1 {
            transform: translateX(-120px);
            animation-delay: 0s;
          }

          .icon-2 {
            transform: translateX(90px);
            animation-delay: 0.5s;
          }

          .icon-3 {
            transform: translateX(-180px);
            animation-delay: 1s;
          }

          .icon-4 {
            transform: translateX(150px);
            animation-delay: 1.5s;
          }

          .icon-5 {
            transform: translateX(-60px);
            animation-delay: 2s;
          }

          .icon-6 {
            transform: translateX(30px);
            animation-delay: 2.5s;
          }

          @keyframes floatGift {
            0%, 100% {
              opacity: 0;
              transform: translateX(var(--x, 0)) translateY(0) rotate(0deg) scale(0.8);
            }
            10% {
              opacity: 0.9;
            }
            50% {
              opacity: 1;
              transform: translateX(var(--x, 0)) translateY(-35px) rotate(180deg) scale(1.1);
            }
            90% {
              opacity: 0.9;
            }
          }

          .animated-title {
            font-size: 48px;
            font-weight: 900;
            background: linear-gradient(135deg, #FF6B6B 0%, #FF8E53 50%, #FF6B6B 100%);
            background-size: 200% 200%;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            animation: gradientShift 4s ease infinite;
            margin-bottom: 18px;
            display: inline-block;
            letter-spacing: -0.5px;
            position: relative;
            filter: drop-shadow(0 2px 4px rgba(255,107,107,0.1));
          }

          .animated-subtitle {
            font-size: 18px;
            color: #666;
            max-width: 700px;
            margin: 0 auto;
            line-height: 1.7;
            font-weight: 500;
          }

          /* ================= ANIMATIONS ================= */
          @keyframes gradientShift {
            0% {
              background-position: 0% 50%;
            }
            50% {
              background-position: 100% 50%;
            }
            100% {
              background-position: 0% 50%;
            }
          }

          /* MOBILE GRID FIX - CENTER ALIGNMENT */
          .mobile-grid {
            margin: 0 auto;
            max-width: 100%;
            padding: 0 10px;
          }

          @media (max-width: 767px) {
            .themed-trending-section {
              padding-top: 60px;
              padding-bottom: 70px;
              margin-top: -30px;
              padding-left: 0;
              padding-right: 0;
            }

            .container {
              padding-left: 15px;
              padding-right: 15px;
              max-width: 100%;
            }

            .mobile-grid {
              margin-left: 0;
              margin-right: 0;
              padding: 0;
            }

            .mobile-grid > [class*="col-"] {
              padding-left: 8px;
              padding-right: 8px;
            }

            .gift-icons-wrapper {
              height: 40px;
            }

            .gift-icon {
              font-size: 20px;
            }

            .animated-title {
              font-size: 25px;
              margin-left:30px;
              font-weight: 800;
            }

            .animated-subtitle {
              font-size: 15px;
              max-width: 90%;
              margin: 0 auto;
              padding: 0 10px;
            }

            .themed-trending-section::before,
            .themed-trending-section::after {
              display: none;
            }
          }

          .see-more-btn-theme {
            padding: 16px 50px;
            border-radius: 50px;
            border: none;
            color: #ffffff;
            font-size: 16px;
            font-weight: 800;
            margin-top: 40px;
            background: linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%);
            cursor: pointer;
            transition: all 0.35s ease;
            box-shadow: 
              0 4px 12px rgba(255,107,107,0.3),
              0 8px 24px rgba(255,107,107,0.2);
            letter-spacing: 0.5px;
            display: inline-flex;
            align-items: center;
            gap: 8px;
            position: relative;
            overflow: hidden;
          }

          .see-more-btn-theme::before {
            content: "";
            position: absolute;
            top: 50%;
            left: 50%;
            width: 0;
            height: 0;
            border-radius: 50%;
            background: rgba(255,255,255,0.3);
            transform: translate(-50%, -50%);
            transition: width 0.5s ease, height 0.5s ease;
          }

          .see-more-btn-theme:hover::before {
            width: 300px;
            height: 300px;
          }

          .see-more-btn-theme:hover {
            transform: translateY(-3px);
            box-shadow: 
              0 6px 16px rgba(255,107,107,0.35),
              0 12px 32px rgba(255,107,107,0.25);
          }

          .btn-icon {
            font-size: 18px;
            position: relative;
            z-index: 1;
            animation: bounceIcon 1.5s ease-in-out infinite;
          }

          @keyframes bounceIcon {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-3px);
            }
          }

          .see-more-btn-theme span {
            position: relative;
            z-index: 1;
          }

          .see-more-btn-theme:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
            box-shadow: 0 4px 12px rgba(255,107,107,0.2);
          }

          @media (max-width: 767px) {
            .see-more-btn-theme {
              padding: 14px 40px;
              font-size: 14px;
              margin-top: 30px;
            }
          }
        `}</style>
      </section>
    </>
  );
}

export default TrendingProducts;